import React, { useEffect, useMemo, useState } from 'react';
import Toast from '@/components/base/Toast';
import bookingService, { type RoomCategoryRecord, type RoomRecord } from '@/services/bookingService';
import eventsCatalogService from '@/services/eventsCatalogService';
import { errorMessage } from '@/lib/api';
import ImageManager, { type PhotoSet } from '@/components/feature/ImageManager';
import BuildingPhoto from '@/components/feature/BuildingPhoto';
import { propertyProfileService } from '@/services/propertyProfileService';
import {
  removeHallImage,
  removeRoomTypeImage,
  uploadHallImage,
  uploadRoomTypeImage,
} from '@/services/catalogImageService';
import type { ConferenceHall } from '@/types';

type HotelSetupTab = 'buildings' | 'floors' | 'rooms' | 'halls' | 'roomcategories' | 'hallcategories';

const TABS: { key: HotelSetupTab; label: string; icon: string }[] = [
  { key: 'buildings', label: 'Buildings', icon: 'ri-community-line' },
  { key: 'floors', label: 'Floors', icon: 'ri-stack-line' },
  { key: 'rooms', label: 'Rooms', icon: 'ri-door-line' },
  { key: 'halls', label: 'Halls', icon: 'ri-building-line' },
  { key: 'roomcategories', label: 'Room Categories', icon: 'ri-hotel-bed-line' },
  { key: 'hallcategories', label: 'Hall Categories', icon: 'ri-grid-line' },
];

const PENTHOUSE_FLOOR = '5';

function floorLabel(floor: string): string {
  return floor === PENTHOUSE_FLOOR ? 'Penthouse' : `Floor ${floor}`;
}

function roomStatusLabel(room: RoomRecord): string {
  if (room.maintenance_status !== 'operational') return 'Maintenance';
  if (room.housekeeping_status === 'dirty') return 'Needs cleaning';
  return room.occupancy_status === 'occupied' ? 'Occupied' : 'Available';
}

const StatusPanel: React.FC<{ icon: string; message: string }> = ({ icon, message }) => (
  <div className="text-center py-12 text-gray-500">
    <i className={`${icon} text-3xl block mb-2`}></i>
    <p className="text-sm font-medium">{message}</p>
  </div>
);

const HotelSetupPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<HotelSetupTab>('buildings');
  const [toast, setToast] = useState<{ message: string; type: 'warning' } | null>(null);
  const [rooms, setRooms] = useState<RoomRecord[]>([]);
  const [categories, setCategories] = useState<RoomCategoryRecord[]>([]);
  const [halls, setHalls] = useState<ConferenceHall[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [roomSearch, setRoomSearch] = useState('');
  const [buildingImages, setBuildingImages] = useState<Record<string, string>>({});

  const updateCategoryPhotos = (id: string, photos: PhotoSet) =>
    setCategories((prev) =>
      prev.map((category) => (category.id === id ? { ...category, ...photos } : category)),
    );

  const updateHallPhotos = (id: string, photos: PhotoSet) =>
    setHalls((prev) => prev.map((hall) => (hall.id === id ? { ...hall, ...photos } : hall)));

  useEffect(() => {
    Promise.all([
      bookingService.getAllRooms(),
      bookingService.getRoomCategories(),
      eventsCatalogService.getConferenceHalls(),
      propertyProfileService.get(),
    ])
      .then(([roomList, categoryList, hallList, profile]) => {
        setRooms(roomList);
        setCategories(categoryList);
        setHalls(hallList);
        setBuildingImages(profile.buildingImages ?? {});
      })
      .catch((error) => {
        setLoadFailed(true);
        setToast({ message: errorMessage(error), type: 'warning' });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const requested = new URLSearchParams(window.location.search).get('tab');
    const tab = TABS.find((item) => item.key === requested);
    if (tab) setActiveTab(tab.key);
  }, []);

  const buildings = useMemo(() => {
    const map = new Map<string, { rooms: number; floors: Set<string> }>();
    for (const room of rooms) {
      const entry = map.get(room.building) ?? { rooms: 0, floors: new Set<string>() };
      entry.rooms += 1;
      entry.floors.add(room.floor);
      map.set(room.building, entry);
    }
    return [...map.entries()]
      .map(([name, entry]) => ({ name, rooms: entry.rooms, floors: entry.floors.size }))
      .sort((a, b) => b.rooms - a.rooms);
  }, [rooms]);

  const floors = useMemo(() => {
    const map = new Map<string, { building: string; floor: string; rooms: number }>();
    for (const room of rooms) {
      const key = `${room.building}|${room.floor}`;
      const entry = map.get(key) ?? { building: room.building, floor: room.floor, rooms: 0 };
      entry.rooms += 1;
      map.set(key, entry);
    }
    return [...map.values()].sort(
      (a, b) => a.building.localeCompare(b.building) || Number(a.floor) - Number(b.floor),
    );
  }, [rooms]);

  const categoryNames = useMemo(() => new Map(categories.map((category) => [category.id, category.name])), [categories]);
  const sortedRooms = useMemo(
    () => [...rooms].sort(
      (a, b) => a.building.localeCompare(b.building)
        || Number(a.floor) - Number(b.floor)
        || a.room_number.localeCompare(b.room_number, undefined, { numeric: true }),
    ),
    [rooms],
  );
  const visibleRooms = useMemo(() => {
    const query = roomSearch.trim().toLowerCase();
    if (!query) return sortedRooms;
    return sortedRooms.filter((room) => room.room_number.toLowerCase().includes(query));
  }, [roomSearch, sortedRooms]);

  const roomCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const room of rooms) counts.set(room.category_id, (counts.get(room.category_id) ?? 0) + 1);
    return counts;
  }, [rooms]);

  const hallCategories = useMemo(() => {
    const map = new Map<string, { name: string; capacity: number; baseRate: number; halls: number }>();
    for (const hall of halls) {
      const entry = map.get(hall.name);
      if (entry) entry.halls += 1;
      else map.set(hall.name, { name: hall.name, capacity: hall.capacity, baseRate: hall.baseRate, halls: 1 });
    }
    return [...map.values()].sort((a, b) => b.capacity - a.capacity);
  }, [halls]);

  const renderPanel = (empty: boolean, emptyMessage: string, content: React.ReactNode) => {
    if (loading) return <StatusPanel icon="ri-loader-4-line animate-spin" message="Loading property inventory…" />;
    if (loadFailed) return <StatusPanel icon="ri-error-warning-line" message="Could not load property inventory. Please retry." />;
    if (empty) return <StatusPanel icon="ri-inbox-line" message={emptyMessage} />;
    return content;
  };

  return (
    <div className="space-y-6 min-w-0 max-w-full overflow-x-hidden">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div>
        <h1 className="text-2xl font-bold text-gray-900">Hotel Setup</h1>
        <p className="text-gray-600">Live property inventory from the hotel management API</p>
        <p className="text-xs text-gray-500 mt-1">This view is read-only. Use the linked management modules for supported changes.</p>
      </div>

      <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex flex-wrap gap-1">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 py-3.5 px-4 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'border-babcock-navy-500 text-babcock-navy-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className={tab.icon}></i>{tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'buildings' && renderPanel(buildings.length === 0, 'No buildings found.', (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Buildings ({buildings.length})</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {buildings.map((building) => (
                  <div key={building.name} className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                    <h4 className="font-semibold text-gray-900">{building.name}</h4>
                    <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-gray-500">Floors:</span> <span className="font-medium">{building.floors}</span></div>
                      <div><span className="text-gray-500">Rooms:</span> <span className="font-medium">{building.rooms}</span></div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <BuildingPhoto
                        name={building.name}
                        url={buildingImages[building.name]}
                        onUploaded={(images) => setBuildingImages(images)}
                        onError={(message) => setToast({ message, type: 'warning' })}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {activeTab === 'floors' && renderPanel(floors.length === 0, 'No floors found.', (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Floors ({floors.length})</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead><tr className="border-b border-gray-100 text-left">
                    <th className="py-3 px-3 text-xs font-medium text-gray-500 uppercase">Floor</th>
                    <th className="py-3 px-3 text-xs font-medium text-gray-500 uppercase">Building</th>
                    <th className="py-3 px-3 text-xs font-medium text-gray-500 uppercase">Rooms</th>
                  </tr></thead>
                  <tbody>{floors.map((floor) => (
                    <tr key={`${floor.building}|${floor.floor}`} className="border-b border-gray-50">
                      <td className="py-3 px-3 text-sm font-medium text-gray-900">{floorLabel(floor.floor)}</td>
                      <td className="py-3 px-3 text-sm text-gray-600">{floor.building}</td>
                      <td className="py-3 px-3 text-sm text-gray-600">{floor.rooms}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          ))}

          {activeTab === 'rooms' && renderPanel(visibleRooms.length === 0, 'No rooms found.', (
            <div>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                <h3 className="text-sm font-semibold text-gray-500 uppercase">Rooms ({visibleRooms.length})</h3>
                <input type="search" value={roomSearch} onChange={(event) => setRoomSearch(event.target.value)} placeholder="Search by room number…" className="w-full sm:w-64 rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                <a href="/back-office/rooms" className="px-3 py-1.5 bg-babcock-navy-700 text-white rounded-lg text-xs font-medium hover:bg-babcock-navy-800">Room Management</a>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead><tr className="border-b border-gray-100 text-left">
                    <th className="py-3 px-3 text-xs font-medium text-gray-500 uppercase">Room</th>
                    <th className="py-3 px-3 text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="py-3 px-3 text-xs font-medium text-gray-500 uppercase">Building</th>
                    <th className="py-3 px-3 text-xs font-medium text-gray-500 uppercase">Floor</th>
                    <th className="py-3 px-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr></thead>
                  <tbody>{visibleRooms.map((room) => (
                    <tr key={room.id} className="border-b border-gray-50">
                      <td className="py-3 px-3 text-sm font-medium text-gray-900">{room.room_number}</td>
                      <td className="py-3 px-3 text-sm text-gray-600">{categoryNames.get(room.category_id) ?? '—'}</td>
                      <td className="py-3 px-3 text-sm text-gray-600">{room.building}</td>
                      <td className="py-3 px-3 text-sm text-gray-600">{floorLabel(room.floor)}</td>
                      <td className="py-3 px-3 text-sm text-gray-600">{roomStatusLabel(room)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          ))}

          {activeTab === 'halls' && renderPanel(halls.length === 0, 'No halls found.', (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Halls ({halls.length})</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {halls.map((hall) => (
                  <div key={hall.id} className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                    <h4 className="font-semibold text-gray-900">{hall.name}</h4>
                    <p className="text-xs text-gray-500">{hall.location}</p>
                    <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-gray-500">Capacity:</span> <span className="font-medium">{hall.capacity}</span></div>
                      <div><span className="text-gray-500">Size:</span> <span className="font-medium">{hall.size} m²</span></div>
                      <div><span className="text-gray-500">Base rate:</span> <span className="font-medium">₦{hall.baseRate.toLocaleString()}</span></div>
                      <div><span className="text-gray-500">Amenities:</span> <span className="font-medium">{hall.amenities.length}</span></div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <ImageManager
                        title="Photography"
                        photos={{ images: hall.images ?? [], imageKeys: hall.imageKeys ?? [] }}
                        onUpload={(file) => uploadHallImage(hall.id, file)}
                        onRemove={(key) => removeHallImage(hall.id, key)}
                        onChange={(photos) => updateHallPhotos(hall.id, photos)}
                        onError={(message) => setToast({ message, type: 'warning' })}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {activeTab === 'roomcategories' && renderPanel(categories.length === 0, 'No room categories found.', (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Room Categories ({categories.length})</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead><tr className="border-b border-gray-100 text-left">
                    <th className="py-3 px-3 text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="py-3 px-3 text-xs font-medium text-gray-500 uppercase">Public Rate</th>
                    <th className="py-3 px-3 text-xs font-medium text-gray-500 uppercase">Max Occupancy</th>
                    <th className="py-3 px-3 text-xs font-medium text-gray-500 uppercase">Bed Type</th>
                    <th className="py-3 px-3 text-xs font-medium text-gray-500 uppercase">Rooms</th>
                    <th className="py-3 px-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr></thead>
                  <tbody>{categories.map((category) => (
                    <React.Fragment key={category.id}>
                      <tr className="border-b border-gray-50">
                        <td className="py-3 px-3 text-sm font-medium text-gray-900">{category.name}</td>
                        <td className="py-3 px-3 text-sm text-gray-600">₦{category.public_rate.toLocaleString()}</td>
                        <td className="py-3 px-3 text-sm text-gray-600">{category.max_occupancy}</td>
                        <td className="py-3 px-3 text-sm text-gray-600">{category.bed_type}</td>
                        <td className="py-3 px-3 text-sm text-gray-600">{roomCounts.get(category.id) ?? 0}</td>
                        <td className="py-3 px-3 text-sm text-gray-600 capitalize">{category.status}</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td colSpan={6} className="px-3 pb-4">
                          <ImageManager
                            title={`${category.name} photography`}
                            photos={{ images: category.images ?? [], imageKeys: category.imageKeys ?? [] }}
                            onUpload={(file) => uploadRoomTypeImage(category.id, file)}
                            onRemove={(key) => removeRoomTypeImage(category.id, key)}
                            onChange={(photos) => updateCategoryPhotos(category.id, photos)}
                            onError={(message) => setToast({ message, type: 'warning' })}
                          />
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}</tbody>
                </table>
              </div>
            </div>
          ))}

          {activeTab === 'hallcategories' && renderPanel(hallCategories.length === 0, 'No hall categories found.', (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Hall Categories ({hallCategories.length})</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead><tr className="border-b border-gray-100 text-left">
                    <th className="py-3 px-3 text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="py-3 px-3 text-xs font-medium text-gray-500 uppercase">Capacity</th>
                    <th className="py-3 px-3 text-xs font-medium text-gray-500 uppercase">Base Rate</th>
                    <th className="py-3 px-3 text-xs font-medium text-gray-500 uppercase">Halls</th>
                  </tr></thead>
                  <tbody>{hallCategories.map((category) => (
                    <tr key={category.name} className="border-b border-gray-50">
                      <td className="py-3 px-3 text-sm font-medium text-gray-900">{category.name}</td>
                      <td className="py-3 px-3 text-sm text-gray-600">{category.capacity}</td>
                      <td className="py-3 px-3 text-sm text-gray-600">₦{category.baseRate.toLocaleString()}</td>
                      <td className="py-3 px-3 text-sm text-gray-600">{category.halls}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HotelSetupPage;
